import React, { useEffect, useMemo, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { obrasConUbicacion } from '../utils/obrasConUbicacion';

const pinIcon = (active, selected) => {
  const fill = active ? '#2563eb' : '#9ca3af';
  const scale = selected ? 1.25 : 1;
  return L.divIcon({
    className: 'obras-map-pin',
    html: `<svg width="${34 * scale}" height="${44 * scale}" viewBox="0 0 34 44" xmlns="http://www.w3.org/2000/svg" style="filter:drop-shadow(0 2px 3px rgba(0,0,0,.35))"><path d="M17 43s15-14.6 15-26A15 15 0 0 0 2 17c0 11.4 15 26 15 26z" fill="${fill}" stroke="#fff" stroke-width="2.5"/><circle cx="17" cy="17" r="5.5" fill="#fff"/></svg>`,
    iconSize: [34 * scale, 44 * scale],
    iconAnchor: [17 * scale, 43 * scale],
    popupAnchor: [0, -40 * scale],
  });
};

const escapeHtml = (s) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const popupHtml = (obra) =>
  `<div style="min-width:140px"><strong>${escapeHtml(obra.nombre)}</strong>${
    obra.direccion ? `<br/><span style="color:#4b5563">${escapeHtml(obra.direccion)}</span>` : ''
  }<br/><span style="color:${obra.active ? '#16a34a' : '#dc2626'}">${obra.active ? 'Activa' : 'Inactiva'}</span></div>`;

/**
 * Map with one pin per obra that has a location.
 * props: obras (Obra[]), selectedId (string | null), onSelect(id)
 */
const ObrasMap = ({ obras, selectedId, onSelect }) => {
  const box = useRef(null);
  const map = useRef(null);
  const layer = useRef(null);
  const markers = useRef({});
  const selectRef = useRef(onSelect);
  selectRef.current = onSelect;

  const puntos = useMemo(() => obrasConUbicacion(obras), [obras]);

  useEffect(() => {
    const m = L.map(box.current, { zoomControl: true, scrollWheelZoom: false, tap: true });
    L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}', {
      maxZoom: 19,
      attribution: 'Tiles &copy; Esri &mdash; Esri, HERE, Garmin, OpenStreetMap contributors',
    }).addTo(m);
    m.setView([-34.6037, -58.3816], 4);
    layer.current = L.layerGroup().addTo(m);
    map.current = m;
    // The modal lays out after mount: Leaflet has to re-measure
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => m.invalidateSize()) : null;
    if (ro) ro.observe(box.current);
    const t = setTimeout(() => m.invalidateSize(), 150);
    return () => {
      clearTimeout(t);
      if (ro) ro.disconnect();
      m.stop(); // cancel in-flight flyTo so its transition end does not fire on a removed map
      m.remove();
      map.current = null;
      layer.current = null;
      markers.current = {};
    };
  }, []);

  useEffect(() => {
    const m = map.current;
    if (!m || !layer.current) return;
    layer.current.clearLayers();
    markers.current = {};
    puntos.forEach((obra) => {
      const mk = L.marker([obra.lat, obra.lng], { icon: pinIcon(obra.active, false), title: obra.nombre })
        .bindPopup(popupHtml(obra))
        .on('click', () => selectRef.current && selectRef.current(obra.id))
        .addTo(layer.current);
      markers.current[obra.id] = { mk, obra };
    });
    if (puntos.length === 1) m.setView([puntos[0].lat, puntos[0].lng], 16);
    else if (puntos.length > 1) m.fitBounds(L.latLngBounds(puntos.map((p) => [p.lat, p.lng])), { padding: [40, 40], maxZoom: 16 });
  }, [puntos]);

  useEffect(() => {
    const m = map.current;
    Object.entries(markers.current).forEach(([id, { mk, obra }]) => {
      mk.setIcon(pinIcon(obra.active, id === selectedId));
      mk.setZIndexOffset(id === selectedId ? 1000 : 0);
    });
    const sel = selectedId && markers.current[selectedId];
    if (m && sel) {
      m.flyTo([sel.obra.lat, sel.obra.lng], Math.max(m.getZoom(), 15), { duration: 0.6 });
      sel.mk.openPopup();
    }
  }, [selectedId, puntos]);

  return (
    <div
      ref={box}
      className="w-full h-64 md:h-80 rounded-xl overflow-hidden border border-gray-200 z-0"
      style={{ background: '#e5e7eb' }}
      role="region"
      aria-label="Mapa de obras"
    />
  );
};

export default ObrasMap;
